import React from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity, Platform } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import colors from '../../lib/colors';
import {
    type LocalizedMessage,
    useOptionalResolvedMessage,
} from '../../lib/i18n/messages';

type BannerType = 'error' | 'warning' | 'info' | 'success' | 'loading';
type BannerVariant = 'filled' | 'subtle' | 'bordered' | 'minimal';

interface BannerAction {
    label: LocalizedMessage;
    onPress: () => void;
}

interface BannerProps {
    type: BannerType;
    variant?: BannerVariant;
    title?: LocalizedMessage;
    message?: LocalizedMessage;
    children?: React.ReactNode;
    action?: BannerAction;
    dismissable?: boolean;
    onDismiss?: () => void;
    style?: any;
    testID?: string;
}

const BANNER_CONFIG = {
    error: {
        icon: 'circle-xmark' as const,
        filled: {
            backgroundColor: '#fef5f5',
            borderColor: '#fee',
            iconBackgroundColor: '#ef4444',
            iconColor: '#ffffff',
            titleColor: '#dc2626',
            textColor: '#6b7280',
        },
        subtle: {
            backgroundColor: '#fef5f5',
            borderColor: '#fdd',
            iconBackgroundColor: '#fee2e2',
            iconColor: '#dc2626',
            titleColor: '#dc2626',
            textColor: '#6b7280',
        },
        bordered: {
            backgroundColor: '#ffffff',
            borderColor: '#fecaca',
            iconBackgroundColor: '#fee2e2',
            iconColor: '#dc2626',
            titleColor: '#dc2626',
            textColor: '#6b7280',
        },
        minimal: {
            backgroundColor: '#ffffff',
            borderColor: '#e5e7eb',
            iconBackgroundColor: '#f3f4f6',
            iconColor: '#ef4444',
            titleColor: '#1f2937',
            textColor: '#6b7280',
        },
    },
    warning: {
        icon: 'triangle-exclamation' as const,
        filled: {
            backgroundColor: '#fffbf0',
            borderColor: '#fff9e6',
            iconBackgroundColor: '#f59e0b',
            iconColor: '#ffffff',
            titleColor: '#d97706',
            textColor: '#6b7280',
        },
        subtle: {
            backgroundColor: '#fffbf0',
            borderColor: '#fef3c7',
            iconBackgroundColor: '#fef3c7',
            iconColor: '#d97706',
            titleColor: '#d97706',
            textColor: '#6b7280',
        },
        bordered: {
            backgroundColor: '#ffffff',
            borderColor: '#fde68a',
            iconBackgroundColor: '#fef3c7',
            iconColor: '#f59e0b',
            titleColor: '#d97706',
            textColor: '#6b7280',
        },
        minimal: {
            backgroundColor: '#ffffff',
            borderColor: '#e5e7eb',
            iconBackgroundColor: '#f3f4f6',
            iconColor: '#f59e0b',
            titleColor: '#1f2937',
            textColor: '#6b7280',
        },
    },
    info: {
        icon: 'circle-info' as const,
        filled: {
            backgroundColor: '#f0f7ff',
            borderColor: '#e6f2ff',
            iconBackgroundColor: '#3b82f6',
            iconColor: '#ffffff',
            titleColor: '#2563eb',
            textColor: '#6b7280',
        },
        subtle: {
            backgroundColor: '#f0f7ff',
            borderColor: '#dbeafe',
            iconBackgroundColor: '#dbeafe',
            iconColor: '#2563eb',
            titleColor: '#2563eb',
            textColor: '#6b7280',
        },
        bordered: {
            backgroundColor: '#ffffff',
            borderColor: '#bfdbfe',
            iconBackgroundColor: '#dbeafe',
            iconColor: '#3b82f6',
            titleColor: '#2563eb',
            textColor: '#6b7280',
        },
        minimal: {
            backgroundColor: '#ffffff',
            borderColor: '#e5e7eb',
            iconBackgroundColor: '#f3f4f6',
            iconColor: '#3b82f6',
            titleColor: '#1f2937',
            textColor: '#6b7280',
        },
    },
    success: {
        icon: 'circle-check' as const,
        filled: {
            backgroundColor: '#f0fdf7',
            borderColor: '#e6fcf2',
            iconBackgroundColor: colors.primaryGreen,
            iconColor: '#ffffff',
            titleColor: colors.primaryGreenDark,
            textColor: '#6b7280',
        },
        subtle: {
            backgroundColor: '#f0fdf7',
            borderColor: '#dcfce7',
            iconBackgroundColor: '#dcfce7',
            iconColor: colors.primaryGreen,
            titleColor: colors.primaryGreenDark,
            textColor: '#6b7280',
        },
        bordered: {
            backgroundColor: '#ffffff',
            borderColor: '#bbf7d0',
            iconBackgroundColor: '#dcfce7',
            iconColor: colors.primaryGreen,
            titleColor: colors.primaryGreenDark,
            textColor: '#6b7280',
        },
        minimal: {
            backgroundColor: '#ffffff',
            borderColor: '#e5e7eb',
            iconBackgroundColor: '#f3f4f6',
            iconColor: colors.primaryGreen,
            titleColor: '#1f2937',
            textColor: '#6b7280',
        },
    },
    loading: {
        icon: 'spinner' as const,
        filled: {
            backgroundColor: '#f0fdf7',
            borderColor: '#e6fcf2',
            iconBackgroundColor: colors.primaryGreen,
            iconColor: '#ffffff',
            titleColor: colors.primaryGreenDark,
            textColor: '#6b7280',
        },
        subtle: {
            backgroundColor: '#f9fafb',
            borderColor: '#e5e7eb',
            iconBackgroundColor: '#e5e7eb',
            iconColor: colors.primaryGreen,
            titleColor: '#374151',
            textColor: '#6b7280',
        },
        bordered: {
            backgroundColor: '#ffffff',
            borderColor: '#bbf7d0',
            iconBackgroundColor: '#dcfce7',
            iconColor: colors.primaryGreen,
            titleColor: '#374151',
            textColor: '#6b7280',
        },
        minimal: {
            backgroundColor: '#ffffff',
            borderColor: '#e5e7eb',
            iconBackgroundColor: '#f3f4f6',
            iconColor: colors.primaryGreen,
            titleColor: '#1f2937',
            textColor: '#6b7280',
        },
    },
};

export default function Banner({
    type,
    variant = 'subtle',
    title,
    message,
    children,
    action,
    dismissable = false,
    onDismiss,
    style,
    testID,
}: BannerProps) {
    const [isDismissed, setIsDismissed] = React.useState(false);
    const resolvedTitle = useOptionalResolvedMessage(title);
    const resolvedMessage = useOptionalResolvedMessage(message);
    const resolvedActionLabel = useOptionalResolvedMessage(action?.label);
    const typeConfig = BANNER_CONFIG[type];
    const variantConfig = typeConfig[variant];

    const handleDismiss = () => {
        setIsDismissed(true);
        if (onDismiss) {
            onDismiss();
        }
    };

    if (isDismissed) {
        return null;
    }

    return (
        <View testID={testID} style={[
            styles.container,
            {
                backgroundColor: variantConfig.backgroundColor,
                borderColor: variantConfig.borderColor,
            },
            style
        ]}>
            {/* Dismiss button - absolute positioned in top-right */}
            {(dismissable || onDismiss) && (
                <TouchableOpacity onPress={handleDismiss} style={styles.dismissButton}>
                    <FontAwesome6 name="x" size={16} color={variantConfig.textColor} />
                </TouchableOpacity>
            )}

            <View style={styles.content}>
                {/* Icon with circular background */}
                <View style={[
                    styles.iconContainer,
                    { backgroundColor: variantConfig.iconBackgroundColor }
                ]}>
                    {type === 'loading' ? (
                        <ActivityIndicator size="small" color={variantConfig.iconColor} />
                    ) : (
                        <FontAwesome6
                            name={typeConfig.icon}
                            size={18}
                            color={variantConfig.iconColor}
                        />
                    )}
                </View>

                {/* Text content */}
                <View style={styles.textContainer}>
                    {resolvedTitle && (
                        <Text style={[styles.title, { color: variantConfig.titleColor }]}>
                            {resolvedTitle}
                        </Text>
                    )}
                    {resolvedMessage && (
                        <Text style={[styles.message, { color: variantConfig.textColor }]}>
                            {resolvedMessage}
                        </Text>
                    )}
                    {children && (
                        <View style={styles.childrenContainer}>
                            {children}
                        </View>
                    )}
                </View>

                {/* Action button - right aligned, vertically centered */}
                {action && (
                    <View style={styles.actionButtonContainer}>
                        <TouchableOpacity
                            onPress={action.onPress}
                            style={[styles.actionButton, { borderColor: variantConfig.borderColor }]}
                            activeOpacity={0.7}
                        >
                            <Text style={[styles.actionButtonText, { color: variantConfig.titleColor }]}>
                                {resolvedActionLabel}
                            </Text>
                        </TouchableOpacity>
                    </View>
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        position: 'relative',
        borderWidth: 1,
        borderRadius: 16,
        padding: 16,
        paddingRight: 48,
        marginVertical: 8,
        marginHorizontal: 0,
    },
    content: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    iconContainer: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 14,
        flexShrink: 0,
    },
    textContainer: {
        flex: 1,
        minWidth: 200,
    },
    title: {
        fontSize: 15,
        fontWeight: '600',
        marginBottom: 4,
        lineHeight: 20,
    },
    message: {
        fontSize: 14,
        lineHeight: 21,
    },
    childrenContainer: {
        marginTop: 4,
    },
    dismissButton: {
        position: 'absolute',
        top: 12,
        right: 12,
        width: 32,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    actionButtonContainer: {
        marginLeft: 'auto',
        paddingLeft: 12,
        flexShrink: 0,
    },
    actionButton: {
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 8,
        borderWidth: 1,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    actionButtonText: {
        fontSize: 14,
        fontWeight: '600',
    },
});
